import * as React from "react";
import cytoscape, { EdgeHandlesApi } from "cytoscape";
import CytoscapeComponent from "react-cytoscapejs";
import dagre from "cytoscape-dagre";
import edgehandles from "cytoscape-edgehandles";
import { Person, PersonRelation } from "../Store/People";
import { Button, ButtonGroup, Segment } from "@/components/bp/bp-ui";
import { PedigreeGraphStyles } from "./PedigreeGraphStyles";
import { BPButton } from "@/components/bp/bpbutton";

cytoscape.use(dagre);

interface PedigreeGraphProps {
  people: Person[];
  relations: PersonRelation[];
  onPersonSelect?: (person?: Person) => void;
  onRelationAdd?: (parent: Person, child: Person) => void;
  onRelationRemove?: (id: string) => void;
  onRelationEdit?: (id: string) => void;
  readOnly?: boolean;
  onPng?: (base64) => void;
  renderPng?: boolean;
}


const layout = {
  name: "dagre",
  rankDir: "TB",
  directed: true,
  grid: true,
  animate: false,
  nodeDimensionsIncludeLabels: true,
};

const animateLayout = {
  ...layout,
  animate: true,
};

export const PedigreeGraph = (props: PedigreeGraphProps, ref) => {
  const { people, relations, onPersonSelect, onRelationAdd } = props;

  const cyRef = React.useRef<cytoscape.Core>();
  const handlesRef = React.useRef<EdgeHandlesApi>();
  const [selectedRelation, setSelectedRelation] = React.useState<string>()

  function refreshLayout() {
    cyRef.current?.layout(layout).run();
    cyRef.current?.fit();
  }

  // cleanup cytoscape listeners on unmount
  React.useEffect(() => {
    return () => {
      if (cyRef.current) {
        cyRef.current.removeAllListeners();
        cyRef.current = null;
      }
    };
  }, []);

  const elements = React.useMemo(() => {
    const peopleElements: cytoscape.ElementDefinition[] = people.map(
      (p) => ({ data: { type: "person", id: p.personId, label: p.displayName, sex: p.sex, missing: p.missing, tested: p.imported } }),
    );

    const relationElements: cytoscape.ElementDefinition[] = relations.map(
      (r) => ({ data: { id: r.relationId, type: "relation", source: r.ancestor, target: r.descendant, questioned: r.questioned } }),
    );

    const allElements = peopleElements.concat(relationElements);
    return allElements;
  }, [people, relations]);

  const getEdgeType = React.useCallback((parentId: string, childId: string) => {
    // console.log(`getEdgeType ${parentId}->${childId}`, relations);
    // Don't allow adding a duplicate relation
    if (relations.find((r) => r.ancestor === parentId && r.descendant === childId)) {
      // console.log(`${parentId}->${childId}: Relation exists`);
      return undefined;
    }

    const existingParents = relations.filter((r) => r.descendant === childId);

    // A person can only have two parents
    if (existingParents.length >= 2) {
      // console.log(`${parentId}->${childId}: Target has two parents`);
      return undefined;
    }

    if (props.readOnly)
      return undefined;

    // console.log(`${parentId}->${childId}: Can Add`);
    return "flat";
  }, [relations]);

  const selectPerson = React.useCallback((id: string) => {
    if (onPersonSelect) {
      const person = people.find((p) => p.personId === id);
      onPersonSelect(person);
    }
  }, [people, onPersonSelect]);

  const selectRelation = React.useCallback((id: string) => {
    setSelectedRelation(id)
  }, [people, onPersonSelect]);

  const addedEdge = React.useCallback((parentId: string, childId: string, added: cytoscape.EdgeCollection) => {
    if (onRelationAdd) {
      const parent = people.find((p) => p.personId === parentId);
      const child = people.find((p) => p.personId === childId);

      if (parent && child) {
        onRelationAdd(parent, child);
      }

      cyRef.current?.remove(added);
    }
  }, [people, onRelationAdd]);

  // const updateLayout = React.useEffect(() => {
  //   if (!cyRef.current) {
  //     return;
  //   }

  //   const thisLayout = cyRef.current
  //     .filter("node[type='person'], edge[type='relation']")
  //     .layout(animateLayout);

  //   thisLayout.on("layoutstop", (e) => {
  //     // console.log("layoutstop", e);
  //     // cyRef.current?.fit()
  //     // cyRef.current?.animate({
  //     //   center: {
  //     //     eles: cyRef.current.filter("node[type='person']"),
  //     //   },
  //     //   fit: {
  //     //     eles: cyRef.current.filter("node[type='person']"),
  //     //     padding: 0,
  //     //   },
  //     // });
  //   });
  //   // thisLayout.run();
  //   setTimeout(() => {
  //     layoutRef.current = thisLayout;
  //     layoutRef.current?.run();
  //   }, 1);
  // }, []);

  const cyCallback = React.useCallback((cy: cytoscape.Core) => {

    if (!cy.edgehandles) {
      cytoscape.use(edgehandles as never);
    }

    if (cyRef.current) {
      return;
    }

    cyRef.current = cy;

    cy.on("tap", "node", (e) => {
      if (props.readOnly)
        return;
      selectPerson(e.target.id());
      selectRelation(null);
    });
    cy.on("tap", "edge", (e) => {
      if (props.readOnly)
        return;
      selectRelation(e.target.id());
    });
    cy.on("tap", (e) => {
      if (props.readOnly)
        return;
      if (e.target == cy) {
        selectRelation(null);
        return;
      }
    });

    // cy.on("mouseout", "node", () => {
    //   handlesRef.current?.hide();
    // });
    cy.on("layoutstart", (e) => {
      handlesRef.current?.hide();
    });
    cy.on("layoutstop", (e) => {
      handlesRef.current?.hide();
      var png = cyRef.current?.png();
      if (props.onPng)
        props.onPng(png);
    });



    // hack to refresh layout after initial render
    cy.ready(() => {
      setTimeout(() => {
        refreshLayout();
      }, 1000);
    });

    cy.on('resize', _evt => {
      refreshLayout();
    })

    if (handlesRef.current) {
      handlesRef.current.hide();
      handlesRef.current.disable();
      handlesRef.current.destroy();
    }

    refreshLayout();

    if (props.readOnly)
      return;

    handlesRef.current = cy.edgehandles({
      handleNodes: "node[type='person']",
      edgeType: (source, target) => {
        if (source?.data() && target?.data()) {
          const parentId = source.data().id as string;
          const childId = target.data().id as string;
          const edge = getEdgeType(parentId, childId);
          return edge;
        }
        return "flat";
      },
      complete: (source, target, added) => {
        addedEdge(source.data().id as string, target.data().id as string, added);
      },
    });

  }, [getEdgeType, addedEdge, selectPerson]);



  return <Segment attached compact basic>
    <div style={{ textAlign: "right" }}>
      {!props.readOnly && selectedRelation != null && <><ButtonGroup basic>
        <BPButton compact type="button" title={'Edit Relation'} icon={"edit"} onClick={() => {
          if (props.onRelationEdit)
            props.onRelationEdit(selectedRelation)
        }}></BPButton>
        <BPButton compact type="button" negative title={'Remove Relation'} icon={"remove"} onClick={() => {
          if (props.onRelationRemove)
            props.onRelationRemove(selectedRelation)
          setSelectedRelation(null)
        }}></BPButton>
      </ButtonGroup>
        {' '}
      </>
      }
      <ButtonGroup basic>
        <BPButton compact type="button" title={'Reset Viewport'} icon={"refresh"} onClick={() => {
          refreshLayout();
        }}></BPButton>
      </ButtonGroup>
    </div>
    <CytoscapeComponent userZoomingEnabled={!props.readOnly} userPanningEnabled={!props.readOnly} cy={cyCallback} stylesheet={PedigreeGraphStyles} layout={layout} elements={elements} style={{ height: "600px" }} />
  </Segment>;
};

// export const PedigreeGraph2 = (props: PedigreeGraphProps, ref) => {
//   const cyRef = React.useRef<cytoscape.Core>();
//   const { people, relations, onPersonSelect, onRelationAdd } = props;

//   const elements = React.useMemo(() => {
//     const peopleElements: cytoscape.ElementDefinition[] = people.map(
//       (p) => ({ data: { type: "person", id: p.personId, label: p.displayName, sex: p.sex, missing: p.missing, tested: p.imported } }),
//     );

//     const relationElements: cytoscape.ElementDefinition[] = relations.map(
//       (r) => ({ data: { id: r.relationId, type: "relation", source: r.ancestor, target: r.descendant, questioned: r.questioned } }),
//     );

//     const allElements = peopleElements.concat(relationElements);
//     // console.log("elements", allElements);
//     return allElements;
//   }, [people, relations]);

//   const cyCallback = React.useCallback((cy: cytoscape.Core) => {
//     if (!cy.edgehandles) {
//       // eslint-disable-next-line @typescript-eslint/no-explicit-any
//       cytoscape.use(edgehandles as never);
//     }

//     if (!cyRef.current) {
//       cyRef.current = cy;
//     }
//   }, []);

//   return <CytoscapeComponent elements={elements} cy={cyCallback} style={{ width: '600px', height: '600px' }} layout={layout} />;
// }
